/**
 * Unified Logging System for ShipmentCoCaptain UI
 * 
 * This logger handles both development and production logging with different strategies:
 * - Development: Full console.log output for debugging
 * - Production: Minimal console output, structured logs for Grafana/Loki ingestion
 */

export interface LogContext {
  userEmail?: string;
  displayName?: string;
  sessionId?: string;
  conversationId?: string;
  messageId?: string;
  jobId?: string;
  timestamp: string;
  component: string;
  action: string;
  environment: string;
  version: string;
  userAgent?: string;
  ipAddress?: string;
}

export interface ChatLogData extends LogContext {
  messageType: 'user' | 'assistant' | 'system';
  messageContent?: string;
  messageLength?: number;
  responseTime?: number;
  hasError?: boolean;
  errorMessage?: string;
  metadata?: Record<string, any>;
}

export interface AuthLogData extends LogContext {
  authAction: 'login_attempt' | 'login_success' | 'login_failed' | 'authorization_check' | 'authorization_granted' | 'authorization_denied' | 'logout' | 'unknown';
  isAuthorized?: boolean;
  authMethod?: 'group' | 'email' | 'none';
  errorMessage?: string;
  metadata?: Record<string, any>;
}

export interface ApiLogData extends LogContext {
  method: string;
  endpoint: string;
  statusCode?: number;
  requestSize?: number;
  responseSize?: number;
  duration?: number;
  errorMessage?: string;
}

export interface UILogData extends LogContext {
  uiAction: 'page_view' | 'button_click' | 'form_submit' | 'error' | 'performance' | 'interaction';
  elementId?: string;
  pageName?: string;
  performanceMetric?: string;
  value?: number;
  errorMessage?: string;
  metadata?: Record<string, any>;
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private isDevelopment: boolean;
  private isProduction: boolean;
  private serviceName = 'shipment-cocaptain-ui';
  private version: string;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isProduction = process.env.NODE_ENV === 'production';
    this.version = process.env.REACT_APP_VERSION || '1.0.0';
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getUserAgent(): string {
    return navigator.userAgent || 'Unknown';
  }

  private getClientIP(): string {
    // In production, this would be set by your reverse proxy/load balancer
    return 'client_ip_not_available';
  }

  private createBaseContext(component: string, action: string): LogContext {
    return {
      timestamp: new Date().toISOString(),
      component,
      action,
      environment: process.env.NODE_ENV || 'development',
      version: this.version,
      userAgent: this.getUserAgent(),
      ipAddress: this.getClientIP(),
    };
  }

  private formatLogForGrafana(level: LogLevel, logData: any): void {
    const grafanaLog = {
      timestamp: logData.timestamp,
      level: level.toUpperCase(),
      service: this.serviceName,
      environment: logData.environment,
      version: logData.version,
      ...logData,
    };

    // Remove undefined/null values for cleaner logs
    const cleanLogEntry = JSON.parse(JSON.stringify(grafanaLog, (key, value) => {
      return value === undefined || value === null ? undefined : value;
    }));

    const logMessage = `[SHIPMENT_COCAPTAIN_${level.toUpperCase()}] ${JSON.stringify(cleanLogEntry)}`;

    // Check if console logging is explicitly enabled in production
    const enableProdConsole = process.env.REACT_APP_ENABLE_PROD_CONSOLE === 'true';
    
    // In production, suppress ALL console output unless explicitly enabled
    // This ensures end users don't see any debug information
    if (this.isProduction && !enableProdConsole) {
      // No console output in production by default
      // Logs are still available via Grafana/Loki for monitoring
      return;
    }

    // In development or when prod console is enabled, log to console
    if (!this.isProduction || enableProdConsole) {
      switch (level) {
        case 'debug':
          console.debug(logMessage);
          break;
        case 'info':
          console.log(logMessage);
          break;
        case 'warn':
          console.warn(logMessage);
          break;
        case 'error':
          console.error(logMessage);
          break;
      }
    }
  }

  // Development-only debug logging
  debug(message: string, data?: any): void {
    if (this.isDevelopment) {
      const logData = {
        ...this.createBaseContext('debug', 'debug_log'),
        message,
        data,
      };
      this.formatLogForGrafana('debug', logData);
    }
  }

  // General info logging
  info(message: string, data?: any): void {
    const logData = {
      ...this.createBaseContext('general', 'info_log'),
      message,
      data,
    };
    this.formatLogForGrafana('info', logData);
  }

  // Warning logging
  warn(message: string, data?: any): void {
    const logData = {
      ...this.createBaseContext('general', 'warning'),
      message,
      data,
    };
    this.formatLogForGrafana('warn', logData);
  }

  // Error logging
  error(message: string, error?: any, context?: any): void {
    const logData = {
      ...this.createBaseContext('general', 'error'),
      message,
      errorMessage: error instanceof Error ? error.message : String(error),
      errorStack: error instanceof Error ? error.stack : undefined,
      context,
    };
    this.formatLogForGrafana('error', logData);
  }

  // Chat-specific logging
  logChatMessage(data: Partial<ChatLogData>): void {
    const logData: ChatLogData = {
      ...this.createBaseContext('chat', 'message'),
      messageType: data.messageType || 'user',
      messageContent: this.isProduction ? undefined : data.messageContent, // Don't log content in prod for privacy
      messageLength: data.messageContent?.length || 0,
      responseTime: data.responseTime,
      hasError: data.hasError || false,
      errorMessage: data.errorMessage,
      metadata: data.metadata,
      ...data,
    };
    this.formatLogForGrafana('info', logData);
  }

  // Authentication logging
  logAuth(data: Partial<AuthLogData>): void {
    const logData: AuthLogData = {
      ...this.createBaseContext('authentication', data.authAction || 'unknown'),
      authAction: data.authAction || 'unknown',
      isAuthorized: data.isAuthorized,
      authMethod: data.authMethod,
      errorMessage: data.errorMessage,
      ...data,
    };
    this.formatLogForGrafana(data.authAction?.includes('failed') || data.authAction?.includes('denied') ? 'warn' : 'info', logData);
  }

  // API call logging
  logApiCall(data: Partial<ApiLogData>): void {
    const logData: ApiLogData = {
      ...this.createBaseContext('api', 'http_request'),
      method: data.method || 'GET',
      endpoint: data.endpoint || 'unknown',
      statusCode: data.statusCode,
      requestSize: data.requestSize,
      responseSize: data.responseSize,
      duration: data.duration,
      errorMessage: data.errorMessage,
      ...data,
    };
    this.formatLogForGrafana(data.statusCode && data.statusCode >= 400 ? 'error' : 'info', logData);
  }

  // UI interaction logging
  logUIAction(data: Partial<UILogData>): void {
    const logData: UILogData = {
      ...this.createBaseContext('ui', data.uiAction || 'interaction'),
      uiAction: data.uiAction || 'interaction',
      elementId: data.elementId,
      pageName: data.pageName,
      performanceMetric: data.performanceMetric,
      value: data.value,
      errorMessage: data.errorMessage,
      ...data,
    };
    this.formatLogForGrafana(data.uiAction === 'error' ? 'error' : 'info', logData);
  }

  // Conversation lifecycle logging
  logConversationStart(userEmail: string, conversationId: string, sessionId?: string): void {
    this.logChatMessage({
      userEmail,
      conversationId,
      sessionId,
      action: 'conversation_start',
      messageType: 'system',
    });
  }

  logConversationEnd(userEmail: string, conversationId: string, messageCount: number, sessionId?: string): void {
    this.logChatMessage({
      userEmail,
      conversationId,
      sessionId,
      action: 'conversation_end',
      messageType: 'system',
      metadata: { messageCount },
    });
  }

  // User query logging with response tracking
  logUserQuery(userEmail: string, conversationId: string, messageId: string, query: string, sessionId?: string): void {
    this.logChatMessage({
      userEmail,
      conversationId,
      messageId,
      sessionId,
      messageType: 'user',
      messageContent: query,
      action: 'user_query',
    });
  }

  logAssistantResponse(userEmail: string, conversationId: string, messageId: string, responseLength: number, responseTime: number, hasError: boolean, sessionId?: string, jobId?: string): void {
    this.logChatMessage({
      userEmail,
      conversationId,
      messageId,
      sessionId,
      jobId,
      messageType: 'assistant',
      messageLength: responseLength,
      responseTime,
      hasError,
      action: 'assistant_response',
    });
  }

  // Error tracking with context
  logUserError(userEmail: string, error: string, context?: any, conversationId?: string, sessionId?: string): void {
    this.logChatMessage({
      userEmail,
      conversationId,
      sessionId,
      messageType: 'system',
      action: 'user_error',
      hasError: true,
      errorMessage: error,
      metadata: context,
    });
  }

  // Performance tracking
  logPerformance(metric: string, value: number, userEmail?: string, context?: any): void {
    this.logUIAction({
      userEmail,
      uiAction: 'performance',
      performanceMetric: metric,
      value,
      metadata: context,
    });
  }

  // Feature usage tracking
  logFeatureUsage(featureName: string, userEmail?: string, context?: any): void {
    this.logUIAction({
      userEmail,
      uiAction: 'button_click',
      elementId: featureName,
      metadata: context,
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Export types for use in other modules
export default logger;
